import { useEffect, useState } from 'react';
import { ExternalLink, RefreshCw, Loader2, CheckCircle2, AlertCircle, Server } from 'lucide-react';
import { servicesApi } from '../api/client';

const MOCK_SERVICES = {
  dev: [
    { name: 'cloud-server', url: 'https://cloud-server-xxx-dev.run.app', status: 'healthy', latestRevision: 'cloud-server-00042-abc', updatedAt: new Date(Date.now() - 3600000).toISOString() },
    { name: 'cloud-admin', url: 'https://cloud-admin-xxx-dev.run.app', status: 'healthy', latestRevision: 'cloud-admin-00018-def', updatedAt: new Date(Date.now() - 7200000).toISOString() },
    { name: 'cloud-frontend', url: 'https://cloud-frontend-xxx-dev.run.app', status: 'healthy', latestRevision: 'cloud-frontend-00031-ghi', updatedAt: new Date(Date.now() - 1803500).toISOString() },
    { name: 'cloud-api', url: 'https://cloud-api-xxx-dev.run.app', status: 'degraded', latestRevision: 'cloud-api-00012-jkl', updatedAt: new Date(Date.now() - 14400000).toISOString() },
    { name: 'cloud-worker', url: null, status: 'healthy', latestRevision: 'cloud-worker-00008-mno', updatedAt: new Date(Date.now() - 86400000).toISOString() },
  ],
  staging: [
    { name: 'cloud-server', url: 'https://cloud-server-xxx-stg.run.app', status: 'healthy', latestRevision: 'cloud-server-00040-pqr', updatedAt: new Date(Date.now() - 10803500).toISOString() },
    { name: 'cloud-admin', url: 'https://cloud-admin-xxx-stg.run.app', status: 'degraded', latestRevision: 'cloud-admin-00016-stu', updatedAt: new Date(Date.now() - 21600000).toISOString() },
    { name: 'cloud-frontend', url: 'https://cloud-frontend-xxx-stg.run.app', status: 'healthy', latestRevision: 'cloud-frontend-00029-vwx', updatedAt: new Date(Date.now() - 5400000).toISOString() },
    { name: 'cloud-api', url: 'https://cloud-api-xxx-stg.run.app', status: 'healthy', latestRevision: 'cloud-api-00011-yza', updatedAt: new Date(Date.now() - 32400000).toISOString() },
    { name: 'cloud-worker', url: null, status: 'idle', latestRevision: 'cloud-worker-00007-bcd', updatedAt: new Date(Date.now() - 172803500).toISOString() },
  ],
  production: [
    { name: 'cloud-server', url: 'https://cloud-server.myapp.com', status: 'healthy', latestRevision: 'cloud-server-00038-efg', updatedAt: new Date(Date.now() - 86400000).toISOString() },
    { name: 'cloud-admin', url: 'https://cloud-admin.myapp.com', status: 'healthy', latestRevision: 'cloud-admin-00015-hij', updatedAt: new Date(Date.now() - 172803500).toISOString() },
    { name: 'cloud-frontend', url: 'https://myapp.com', status: 'healthy', latestRevision: 'cloud-frontend-00027-klm', updatedAt: new Date(Date.now() - 172803500).toISOString() },
    { name: 'cloud-api', url: 'https://api.myapp.com', status: 'healthy', latestRevision: 'cloud-api-00010-nop', updatedAt: new Date(Date.now() - 259200000).toISOString() },
    { name: 'cloud-worker', url: null, status: 'idle', latestRevision: 'cloud-worker-00006-qrs', updatedAt: new Date(Date.now() - 604803500).toISOString() },
  ],
};

const STATUS_CONFIG = {
  healthy: { icon: CheckCircle2, cls: 'text-green-400', dot: 'bg-green-400' },
  degraded: { icon: AlertCircle, cls: 'text-red-400', dot: 'bg-red-400' },
  idle: { icon: AlertCircle, cls: 'text-slate-500', dot: 'bg-slate-500' },
};

const ENV_CONFIG = {
  dev: { label: 'Development', color: 'bg-blue-500', text: 'text-blue-400', border: 'border-blue-500/30' },
  staging: { label: 'Staging', color: 'bg-yellow-500', text: 'text-yellow-400', border: 'border-yellow-500/30' },
  production: { label: 'Production', color: 'bg-green-500', text: 'text-green-400', border: 'border-green-500/30' },
};

export default function Services() {
  const [data, setData] = useState(MOCK_SERVICES);
  const [activeEnv, setActiveEnv] = useState('dev');
  const [loading, setLoading] = useState(false);

  const services = data[activeEnv] || [];
  const env = ENV_CONFIG[activeEnv];

  return (
    <div className="p-6 space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Services</h2>
          <p className="text-sm text-slate-500">Cloud Run services across all environments</p>
        </div>
        <button disabled={loading} className="btn-ghost py-1.5 px-3 text-xs">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Env tabs */}
      <div className="flex items-center gap-1 bg-surface-2 rounded-xl p-1 w-fit">
        {Object.entries(ENV_CONFIG).map(([id, cfg]) => (
          <button
            key={id}
            onClick={() => setActiveEnv(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeEnv === id ? 'bg-surface-3 text-slate-900 shadow-sm' : 'text-slate-400 hover:text-slate-900'
            }`}
          >
            <span className={`status-dot ${cfg.color}`} />
            {cfg.label}
          </button>
        ))}
      </div>

      {/* Overview cards */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Services', value: services.length },
          { label: 'Healthy', value: services.filter(s => s.status === 'healthy').length, color: 'text-green-400' },
          { label: 'Degraded', value: services.filter(s => s.status === 'degraded').length, color: 'text-red-400' },
        ].map(stat => (
          <div key={stat.label} className="card px-4 py-3">
            <p className={`text-2xl font-bold ${stat.color || 'text-slate-900'}`}>{stat.value}</p>
            <p className="text-xs text-slate-500">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Service cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {services.map(svc => {
          const sc = STATUS_CONFIG[svc.status] || STATUS_CONFIG.idle;
          const Icon = sc.icon;
          return (
            <div key={svc.name} className={`card p-4 hover:border-slate-300 transition-all`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Server size={16} className="text-slate-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900 font-mono">{svc.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className={`status-dot ${sc.dot} w-1.5 h-1.5`} />
                      <span className={`text-xs ${sc.cls}`}>{svc.status}</span>
                    </div>
                  </div>
                </div>
                {svc.url && (
                  <a
                    href={svc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-500 hover:text-slate-900 transition-colors"
                  >
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">Revision</span>
                  <span className="text-slate-700 font-mono truncate max-w-[180px]">{svc.latestRevision}</span>
                </div>
                {svc.url && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">URL</span>
                    <a href={svc.url} target="_blank" rel="noopener noreferrer" className="text-accent-purple hover:text-accent-purple-light truncate max-w-[180px] transition-colors">
                      {svc.url.replace('https://', '')}
                    </a>
                  </div>
                )}
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">Updated</span>
                  <span className="text-slate-400">
                    {new Date(svc.updatedAt).toLocaleDateString()} {new Date(svc.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
